import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type Element } from 'mdx/types'
import React, { ReactNode } from 'react'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { EmptyState } from '../emptyState/emptyState'
import Icon from '../icon'
import Pagination from '../pagination'
import Spinner from '../spinner/Spinner'

const TableRow: React.FC<{
  id: string | number
  content: (string | number | React.ReactNode)[]
  isRowClickable: boolean
  onRowClick: (id: string | number) => void
  sortable: boolean
  sortableLabel?: string
}> = ({ id, content, isRowClickable, onRowClick, sortable, sortableLabel }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const tdClassName = `
    px-3 py-4
    first:pl-4 first:pr-3 first:sm:pl-3 
    last:pl-3 last:pr-4 last:sm:pr-3
    border-b
    group-last:border-b-0 group-last:pb-0
    border-divider-gray
    whitespace-nowrapd text-sm 
    font-normal text-sec-black
    transition-all duration-300
    overflow-visible
  `

  return (
    <tr
      ref={setNodeRef}
      style={style}
      key={id}
      className={`
        border-spacing-y-80
        group
        ${
          isRowClickable &&
          'cursor-pointer md:hover:scale-[0.99] transition-all duration-300'
        }
      `}
      onClick={() => isRowClickable && onRowClick(id)}
    >
      {sortable && (
        <td className={`${tdClassName} !w-[50px] !max-w-[50px]`}>
          <button
            className="w-fit cursor-grab !max-w-[50px]"
            aria-label={sortableLabel ?? `Reorder row ${String(id)}`}
            {...attributes}
            {...listeners}
            type="button"
          >
            <Icon id="rearrange" width={24} height={24} />
          </button>
        </td>
      )}
      {content.map((cellContent, idx) => {
        return cellContent !== undefined ? (
          <td className={tdClassName} key={`table-row-${id}-cell-${idx}`}>
            {cellContent}
          </td>
        ) : null
      })}
    </tr>
  )
}

export default TableRow

export const TableComponent: React.FC<{
  headers?: (Element | string | undefined)[]
  rows: Array<{
    id: number | string
    content: (Element | string | undefined | number)[]
    sortableLabel?: string
  }>
  name?: string
  showName?: boolean
  isRowClickable?: boolean
  onRowClick?: (idx: number | string) => void
  loading?: boolean
  isEmpty?: boolean
  emptyText?: ReactNode
  currentPage?: number
  totalDataCount?: number
  pageLimit?: number
  pageRangeDisplayed?: number
  onPageChange?: (pageNo: number) => void
  fixedTableWidth?: boolean
  sortable?: boolean
}> = ({
  headers,
  rows,
  name = '',
  showName = false,
  isRowClickable = false,
  onRowClick = () => {},
  loading = false,
  isEmpty = false,
  emptyText = `There's nothing to see here`,
  currentPage,
  totalDataCount,
  pageLimit,
  pageRangeDisplayed = 3,
  onPageChange = () => {},
  fixedTableWidth = false,
  sortable = false,
}) => {
  const thClassName = `
  px-3 py-3.5 
  first:pl-4 first:pr-3 first:sm:pl-3 
  last:pl-3 last:pr-4 last:sm:pr-3
  text-left text-black
  border-b border-divider-gray
  uppercase font-medium
  text-sm
  `
  return loading ? (
    <div className="flex items-center justify-center">
      <Spinner fullScreen={false} />
    </div>
  ) : (
    <>
      {showName && <p className="text-xl font-semibold mb-6">{name}</p>}

      {!isEmpty ? (
        <div className="w-full overflow-x-scroll">
          <table
            className={`
            min-w-full max-w-full
            ${fixedTableWidth && '!table-fixed w-full'}
            `}
          >
            {headers && (
              <thead>
                <tr>
                  {sortable && (
                    <th
                      className={`${thClassName} !max-w-[30px]`}
                      key={`table-${name}-header`}
                    >
                      {' '}
                    </th>
                  )}

                  {headers.map((header, idx) => {
                    const key = `table-${name}-header${idx}`

                    return header !== undefined ? (
                      <th scope="col" className={thClassName} key={key}>
                        {header}
                      </th>
                    ) : undefined
                  })}
                </tr>
              </thead>
            )}

            <tbody className="">
              {rows?.map(({ id, content, sortableLabel }) => (
                <TableRow
                  key={id}
                  id={id}
                  content={content}
                  isRowClickable={isRowClickable}
                  onRowClick={onRowClick}
                  sortable={sortable}
                  sortableLabel={sortableLabel}
                />
              ))}
            </tbody>
          </table>

          {totalDataCount && pageLimit && totalDataCount / pageLimit > 1 ? (
            <Pagination
              currentPage={currentPage ?? 0}
              totalCount={totalDataCount ?? 0}
              itemsPerPage={pageLimit ?? NUMBER_OF_ITEMS_PER_PAGE}
              pageRangeDisplayed={pageRangeDisplayed}
              onPageChange={onPageChange}
              className="mt-10 sticky bottom-0 left-0 right-0"
            />
          ) : undefined}
        </div>
      ) : (
        <EmptyState text={emptyText} />
      )}
    </>
  )
}
